import { Routes, Route, Navigate, useLocation } from "react-router";
import { lazy, Suspense } from "react";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { useAuth } from "./hooks/useAuth";
import { useAdmin } from "./hooks/useAdmin";
import { Loading } from "./components/ui/Loading";
import { ErrorBoundary } from "./components/error/ErrorBoundary";

// Lazy load all page components for better performance
// Layout is lazy-loaded so framer-motion (pulled in by Header/Sidebar/MobileSidebar) stays
// out of the first-paint critical path.
const Layout = lazy(() => import("./components/layout/Layout").then(module => ({ default: module.Layout })));
const LoginPage = lazy(() => import("./pages/LoginPage").then(module => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const RegistrationPage = lazy(() => import("./pages/RegistrationPage").then(module => ({ default: module.RegistrationPage })));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage").then(module => ({ default: module.ForgotPasswordPage })));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage").then(module => ({ default: module.VerifyEmailPage })));
const UnauthorizedPage = lazy(() => import("./pages/UnauthorizedPage").then(module => ({ default: module.UnauthorizedPage })));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const ClientManagement = lazy(() => import("./components/clients/ClientManagement").then(module => ({ default: module.ClientManagement })));
const WorkspacePage = lazy(() => import("./pages/WorkspacePage"));
const BoardBuilderPage = lazy(() => import("./pages/BoardBuilderPage").then(module => ({ default: module.BoardBuilderPage })));
const ImageManagementPage = lazy(() => import("./pages/ImageManagementPage").then(module => ({ default: module.ImageManagementPage })));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then(module => ({ default: module.NotFoundPage })));
const ClientDetailPage = lazy(() => import("./pages/ClientDetailPage"));
const NotesPage = lazy(() => import("./pages/NotesPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then(module => ({ default: module.ProfilePage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(module => ({ default: module.SettingsPage })));
const BillingPage = lazy(() => import("./pages/BillingPage").then(module => ({ default: module.BillingPage })));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage").then(module => ({ default: module.CheckoutSuccessPage })));
const CheckoutCancelPage = lazy(() => import("./pages/CheckoutCancelPage").then(module => ({ default: module.CheckoutCancelPage })));
const ErrorHandlingDemo = lazy(() => import("./components/demo/ErrorHandlingDemo").then(module => ({ default: module.ErrorHandlingDemo })));
const DebugPage = lazy(() => import("./pages/DebugPage").then(module => ({ default: module.DebugPage })));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// Loading component for route transitions
function RouteLoading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loading variant="spinner" size="lg" />
    </div>
  );
}

// Per-route error boundary: catches render/lazy-load errors for the active
// route so one broken page (e.g. the billing infinite-loop crash) can't blank
// the whole app, and resets when the user navigates to a different route.
function LazyRouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <ErrorBoundary key={location.pathname} isolate>
      <Suspense fallback={<RouteLoading />}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
}

// Route-level admin guard: keeps the admin shell from rendering before the
// in-component role check can redirect.
function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useAdmin();

  if (isLoading) {
    return <RouteLoading />;
  }

  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

function ConditionalHome() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Wait for Firebase Auth to restore session before deciding what to show
  if (loading) {
    return <RouteLoading />;
  }

  if (isAuthenticated) {
    return (
      <ProtectedRoute>
        <LazyRouteErrorBoundary>
          <Layout />
        </LazyRouteErrorBoundary>
      </ProtectedRoute>
    );
  }

  // Unauthenticated visitors to the app host go straight to login. The landing
  // page lives on the marketing site (mysensorybook.com), not here. Carry the
  // deep link along so LoginPage can send the user back after signing in.
  return <Navigate to="/login" state={{ from: location }} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <LazyRouteErrorBoundary>
            <LoginPage />
          </LazyRouteErrorBoundary>
        } 
      />
      <Route 
        path="/register" 
        element={
          <LazyRouteErrorBoundary>
            <RegisterPage />
          </LazyRouteErrorBoundary>
        } 
      />
      <Route 
        path="/registration" 
        element={
          <LazyRouteErrorBoundary>
            <RegistrationPage />
          </LazyRouteErrorBoundary>
        } 
      />
      {/* Onboarding walks a new therapist through a real image generation and
          shows their own generated result - it must be behind auth like every
          other page that touches generation, not reachable while logged out. */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <LazyRouteErrorBoundary>
              <OnboardingPage />
            </LazyRouteErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <LazyRouteErrorBoundary>
            <ForgotPasswordPage />
          </LazyRouteErrorBoundary>
        } 
      />
      <Route
        path="/unauthorized"
        element={
          <LazyRouteErrorBoundary>
            <UnauthorizedPage />
          </LazyRouteErrorBoundary>
        }
      />
      <Route
        path="/verify-email"
        element={
          <LazyRouteErrorBoundary>
            <VerifyEmailPage />
          </LazyRouteErrorBoundary>
        }
      />

      {/* Conditional home route */}
      <Route path="/" element={<ConditionalHome />}>
        {/* Protected routes - only accessible when authenticated */}
        <Route 
          index 
          element={
            <LazyRouteErrorBoundary>
              <HomePage />
            </LazyRouteErrorBoundary>
          } 
        />
        <Route 
          path="clients" 
          element={
            <LazyRouteErrorBoundary>
              <ClientManagement />
            </LazyRouteErrorBoundary>
          } 
        />
        <Route 
          path="clients/:clientId" 
          element={
            <LazyRouteErrorBoundary>
              <ClientDetailPage />
            </LazyRouteErrorBoundary>
          } 
        />
         <Route
           path="clients/:clientId/workspace"
           element={
             <LazyRouteErrorBoundary>
               <WorkspacePage />
             </LazyRouteErrorBoundary>
           }
         />
         <Route
           path="clients/:clientId/workspace/create-magic"
           element={
             <LazyRouteErrorBoundary>
               <WorkspacePage />
             </LazyRouteErrorBoundary>
           }
         />
         <Route
           path="clients/:clientId/workspace/sensory-book"
           element={
             <LazyRouteErrorBoundary>
               <WorkspacePage />
             </LazyRouteErrorBoundary>
           }
         />
         <Route
           path="clients/:clientId/workspace/board"
           element={
             <LazyRouteErrorBoundary>
               <BoardBuilderPage />
             </LazyRouteErrorBoundary>
           }
         />
        <Route
          path="clients/:clientId/images"
          element={
            <LazyRouteErrorBoundary>
              <ImageManagementPage />
            </LazyRouteErrorBoundary>
          }
        />
        <Route
          path="clients/:clientId/images/create"
          element={
            <LazyRouteErrorBoundary>
              <ImageManagementPage />
            </LazyRouteErrorBoundary>
          }
        />
        <Route
          path="clients/:clientId/images/edit"
          element={
            <LazyRouteErrorBoundary>
              <ImageManagementPage />
            </LazyRouteErrorBoundary>
          }
        />
        <Route 
          path="clients/:clientId/notes" 
          element={
            <LazyRouteErrorBoundary>
              <NotesPage />
            </LazyRouteErrorBoundary>
          } 
        />

        {/* User routes */}
        <Route 
          path="profile" 
          element={
            <LazyRouteErrorBoundary>
              <ProfilePage />
            </LazyRouteErrorBoundary>
          } 
        />
        <Route
          path="settings"
          element={
            <LazyRouteErrorBoundary>
              <SettingsPage />
            </LazyRouteErrorBoundary>
          }
        />
        <Route
          path="settings/billing"
          element={
            <LazyRouteErrorBoundary>
              <BillingPage />
            </LazyRouteErrorBoundary>
          }
        />
        <Route
          path="checkout/success"
          element={
            <LazyRouteErrorBoundary>
              <CheckoutSuccessPage />
            </LazyRouteErrorBoundary>
          }
        />
        <Route
          path="checkout/cancel"
          element={
            <LazyRouteErrorBoundary>
              <CheckoutCancelPage />
            </LazyRouteErrorBoundary>
          }
        />

        {/* Dev-only routes - never registered in production builds */}
        {import.meta.env.DEV && (
          <>
            <Route
              path="demo/error-handling"
              element={
                <LazyRouteErrorBoundary>
                  <ErrorHandlingDemo />
                </LazyRouteErrorBoundary>
              }
            />
            <Route
              path="debug"
              element={
                <LazyRouteErrorBoundary>
                  <DebugPage />
                </LazyRouteErrorBoundary>
              }
            />
          </>
        )}

        {/* Admin routes - route-level guard plus in-component role check */}
        <Route
          path="admin/*"
          element={
            <RequireAdmin>
              <LazyRouteErrorBoundary>
                <AdminDashboard />
              </LazyRouteErrorBoundary>
            </RequireAdmin>
          }
        />
      </Route>

      {/* Public 404 - top-level so unauthenticated deep links don't route
          through the auth-gated home. */}
      <Route
        path="/404"
        element={
          <LazyRouteErrorBoundary>
            <NotFoundPage />
          </LazyRouteErrorBoundary>
        }
      />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
